<template>
  <div>
    <RadioButtonMeasurementSwitcher
      v-if="canSwitchBmiMeasurementSystem && styleProps.measurementSwitcher == 'radio'"
      v-model="weightMeasurementSystem"
      :measurement-systems="measurementSystems"
      type="weight"
    />
    <div class="flex flew-row">
      <ImperialWeightInput
        :weightInStone.sync="weightInStone"
        :weightInPounds.sync="weightInPounds"
        :input-style="styleProps.inputStyle"
        :measurement-indicators="styleProps.measurementIndicators.imperial"
        @blur="$emit('blur')"
        v-if="weightMeasurementSystem === 'Imperial'"
      />
      <UsCustomaryWeightInput
        :weightInPounds.sync="weightInPounds"
        :input-style="styleProps.inputStyle"
        :measurement-indicators="styleProps.measurementIndicators.imperial"
        @blur="$emit('blur')"
        v-if="weightMeasurementSystem === 'UsCustomary' || weightMeasurementSystem === 'UsImperial'"
      />
      <MetricWeightInput
        :weightInKg.sync="weightInKg"
        :input-style="styleProps.inputStyle"
        :measurement-indicators="styleProps.measurementIndicators.metric"
        @blur="$emit('blur')"
        v-if="weightMeasurementSystem === 'Metric'"
      />
    </div>
    <div v-if="canSwitchBmiMeasurementSystem && styleProps.measurementSwitcher == 'button'">
      <div v-if="weightMeasurementSystemOptions().includes('Metric')">
        <button class="mt-3 measurement-switcher" @click="switchWeightMeasurementSystem('Metric')" type="button">
          Change to kilograms
        </button>
      </div>
      <div v-if="showPoundsSwitch()">
        <button
          class="mt-3 measurement-switcher"
          @click="switchWeightMeasurementSystem(availablePoundsMeasurementSystem())"
          type="button"
        >
          Change to pounds
        </button>
      </div>
      <div v-if="weightMeasurementSystemOptions().includes('Imperial')">
        <button class="mt-3 measurement-switcher" @click="switchWeightMeasurementSystem('Imperial')" type="button">
          Change to stone/pounds
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue, { PropType } from "vue";
import poster from "../../../mixins/poster";
import _ from "lodash";
import {
  ImperialWeightMeasurement,
  MeasurementSystem,
  MetricWeightMeasurement,
  UsCustomaryWeightMeasurement,
  WeightInputStyleProps,
  WeightMeasurement,
} from "../../../types";
import ImperialWeightInput from "./ImperialWeightInput.vue";
import UsCustomaryWeightInput from "./UsCustomaryWeightInput.vue";
import MetricWeightInput from "./MetricWeightInput.vue";
import RadioButtonMeasurementSwitcher from "./RadioButtonMeasurementSwitcher.vue";

export interface WeightValueUpdatedEvent {
  isComplete: boolean;
  isValid: boolean;
  value: WeightMeasurement | null;
}

export default Vue.extend({
  components: {
    RadioButtonMeasurementSwitcher,
    ImperialWeightInput,
    UsCustomaryWeightInput,
    MetricWeightInput,
  },
  mixins: [poster],
  props: {
    measurementSystems: { type: Array as PropType<MeasurementSystem[]>, required: true },
    initialValue: { type: Object as PropType<WeightMeasurement>, required: false },
    styleProps: { type: Object as PropType<WeightInputStyleProps>, required: true },
  },
  data() {
    let data = {
      weightInStone: null,
      weightInPounds: null,
      weightInKg: null,
      weightMeasurementSystem: this.measurementSystems[0],
      canSwitchBmiMeasurementSystem: this.measurementSystems.length > 1,
    };

    if (!!this.initialValue?.type && this.measurementSystems.includes(this.initialValue.type)) {
      // Accept the initial value only if we recognise the measurement system.
      data.weightMeasurementSystem = this.initialValue?.type;
      switch (this.initialValue?.type) {
        case "Metric":
          data.weightInKg = this.initialValue.kg;
          break;
        case "Imperial":
          data.weightInStone = this.initialValue.stone;
          data.weightInPounds = this.initialValue.pounds;
          break;
        case "UsCustomary":
        case "UsImperial":
          data.weightInPounds = this.initialValue.pounds;
          break;
        default:
          if (!this.initialValue) {
            break;
          } else {
            console.error("Unrecognised measurement system " + this.initialValue.type);
            break;
          }
      }
    }

    return data;
  },
  created() {
    this.handleValueUpdate();
  },
  methods: {
    handleValueUpdate() {
      if (!this.isComplete || !this.isValid) {
        this.$emit("value-updated", {
          isComplete: this.isComplete,
          isValid: this.isValid,
          value: null,
        });
        return;
      }

      let weight: WeightMeasurement;

      switch (this.weightMeasurementSystem) {
        case "Imperial":
          weight = this.imperialWeightMeasurement();
          break;
        case "UsCustomary":
        case "UsImperial":
          weight = this.usCustomaryWeightMeasurement();
          break;
        case "Metric":
          weight = this.metricWeightMeasurement();
          break;
        default:
          console.error("Unrecognised measurement system " + this.weightMeasurementSystem);
          break;
      }

      this.$emit("value-updated", {
        isComplete: this.isComplete,
        isValid: this.isValid,
        value: weight,
      });
    },
    switchWeightMeasurementSystem(newSystem: MeasurementSystem) {
      if (!this.canSwitchBmiMeasurementSystem) {
        return;
      }
      this.weightMeasurementSystem = newSystem;
    },
    weightMeasurementSystemOptions() {
      return this.measurementSystems.filter((system) => system != this.weightMeasurementSystem);
    },
    imperialWeightMeasurement(): ImperialWeightMeasurement {
      return {
        type: "Imperial",
        stone: this.weightInStone,
        pounds: this.weightInPounds,
      };
    },
    usCustomaryWeightMeasurement(): UsCustomaryWeightMeasurement {
      return {
        type: "UsCustomary",
        pounds: this.weightInPounds,
      };
    },
    metricWeightMeasurement(): MetricWeightMeasurement {
      return {
        type: "Metric",
        kg: this.weightInKg,
      };
    },
    showPoundsSwitch(): boolean {
      return (
        (this.weightMeasurementSystemOptions().includes("UsCustomary") &&
          this.weightMeasurementSystem !== "UsImperial") ||
        (this.weightMeasurementSystemOptions().includes("UsImperial") && this.weightMeasurementSystem !== "UsCustomary")
      );
    },
    availablePoundsMeasurementSystem(): MeasurementSystem {
      const poundsSystems = ["UsCustomary", "UsImperial"];
      return this.measurementSystems.find((system) => poundsSystems.includes(system)) || "UsCustomary";
    },
  },
  computed: {
    isComplete: function () {
      switch (this.weightMeasurementSystem) {
        case "UsCustomary":
        case "UsImperial":
          return !!_.isNumber(this.weightInPounds);
        case "Imperial":
          return !!(_.isNumber(this.weightInStone) && _.isNumber(this.weightInPounds));
        default:
          return !!_.isNumber(this.weightInKg);
      }
    },
    isValid: function () {
      switch (this.weightMeasurementSystem) {
        case "UsCustomary":
        case "UsImperial":
          return this.weightInPounds > 0;
        case "Imperial":
          return this.weightInStone > 0 && this.weightInPounds >= 0;
        default:
          return this.weightInKg > 0;
      }
    },
  },
  watch: {
    weightInStone: function () {
      this.handleValueUpdate();
    },
    weightInPounds: function () {
      this.handleValueUpdate();
    },
    weightInKg: function () {
      this.handleValueUpdate();
    },
    weightMeasurementSystem: function () {
      this.weightInStone = null;
      this.weightInPounds = null;
      this.weightInKg = null;
    },
  },
});
</script>
